import { memo } from 'react';
import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';
import { getWormholeColor, getEffectIcon } from '../../lib/wormholeData';

export interface PilotInfo {
  characterId: number;
  characterName: string;
  isOnline: boolean;
}

export interface SystemNodeData extends Record<string, unknown> {
  id: string;
  systemId: number;
  systemName: string;
  alias: string | null;
  securityStatus: number | null;
  wormholeClass: string | null;
  effect: string | null;
  locked: boolean;
  pilotCount?: number;
  pilots?: PilotInfo[];
  statics?: string[]; // Static wormhole destinations (e.g., ["C3", "LS"])
}

export type SystemNode = Node<SystemNodeData, 'system'>;

// Security status colors for k-space (background)
function getSecurityBgColor(security: number): string {
  if (security >= 0.5) return 'bg-green-900/80 border-green-600'; // Highsec
  if (security > 0) return 'bg-yellow-900/80 border-yellow-600'; // Lowsec
  return 'bg-red-900/80 border-red-600'; // Nullsec
}

// Get security class name for text color
function getSecurityClassName(security: number): string {
  if (security >= 0.5) return 'HS';
  if (security > 0) return 'LS';
  return 'NS';
}

function SystemNodeComponent({ data, selected }: NodeProps<SystemNode>) {
  const {
    systemName,
    alias,
    securityStatus,
    wormholeClass,
    effect,
    locked,
    pilots = [],
    statics = [],
  } = data as SystemNodeData;

  // Determine background color based on wormhole class or security status
  let bgClass = 'bg-gray-800/90 border-gray-600';
  if (wormholeClass) {
    // Use a neutral dark background for wormhole systems
    bgClass = 'bg-gray-900/95 border-gray-600';
  } else if (securityStatus !== null) {
    bgClass = getSecurityBgColor(securityStatus);
  }

  // Get the colored class text
  const classColor = wormholeClass
    ? getWormholeColor(wormholeClass)
    : securityStatus !== null
      ? getWormholeColor(getSecurityClassName(securityStatus))
      : null;

  // Display name (alias if set, otherwise system name)
  const displayName = alias || systemName;

  // Effect icon
  const effectIcon = getEffectIcon(effect);

  return (
    <div
      className={`
        relative min-w-[180px] rounded-lg border-2 shadow-lg transition-all
        ${bgClass}
        ${selected ? 'ring-2 ring-blue-400 ring-opacity-80' : ''}
        ${locked ? 'opacity-80' : ''}
      `}
    >
      {/* Connection handles - centered on each side */}
      {/* Top */}
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        className="!opacity-0 !w-4 !h-2 !top-0 !left-1/2 !-translate-x-1/2 !border-0 !rounded-none"
      />
      <Handle
        type="source"
        position={Position.Top}
        id="top-source"
        className="!opacity-0 !w-4 !h-2 !top-0 !left-1/2 !-translate-x-1/2 !border-0 !rounded-none"
      />
      {/* Bottom */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        className="!opacity-0 !w-4 !h-2 !bottom-0 !left-1/2 !-translate-x-1/2 !border-0 !rounded-none"
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom-target"
        className="!opacity-0 !w-4 !h-2 !bottom-0 !left-1/2 !-translate-x-1/2 !border-0 !rounded-none"
      />
      {/* Left */}
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        className="!opacity-0 !w-2 !h-4 !left-0 !top-1/2 !-translate-y-1/2 !border-0 !rounded-none"
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left-source"
        className="!opacity-0 !w-2 !h-4 !left-0 !top-1/2 !-translate-y-1/2 !border-0 !rounded-none"
      />
      {/* Right */}
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        className="!opacity-0 !w-2 !h-4 !right-0 !top-1/2 !-translate-y-1/2 !border-0 !rounded-none"
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right-target"
        className="!opacity-0 !w-2 !h-4 !right-0 !top-1/2 !-translate-y-1/2 !border-0 !rounded-none"
      />

      {/* Main content area */}
      <div className="px-3 py-2">
        {/* Top row: Class + Name + Lock */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {/* Wormhole class or security indicator (colored) */}
            {(wormholeClass || securityStatus !== null) && (
              <span
                className="font-bold text-sm shrink-0"
                style={{ color: classColor || '#9ca3af' }}
              >
                {wormholeClass || (securityStatus !== null ? securityStatus.toFixed(1) : '')}
              </span>
            )}

            {/* System name */}
            <span
              className="font-semibold text-white truncate text-sm"
              title={displayName}
            >
              {displayName}
            </span>
          </div>

          {/* Right side indicators */}
          <div className="flex items-center gap-1 shrink-0">
            {/* Effect icon */}
            {effectIcon && (
              <span className="text-xs" title={effect || ''}>
                {effectIcon}
              </span>
            )}

            {/* Lock indicator */}
            {locked && (
              <span className="text-xs" title="Position locked">
                🔒
              </span>
            )}
          </div>
        </div>

        {/* Alias subtitle if present */}
        {alias && (
          <div className="text-xs text-gray-400 truncate mt-0.5" title={systemName}>
            {systemName}
          </div>
        )}

        {/* Status bar - colored line */}
        <div className="mt-2 h-1 rounded-full bg-orange-500/80" title="Connection status" />

        {/* Bottom row: Statics + Pilot count */}
        <div className="flex items-center justify-between mt-2">
          {/* Statics display */}
          <div className="flex items-center gap-1">
            {statics.length > 0 ? (
              statics.map((s, i) => {
                // Get color for each static destination
                const staticColor = getWormholeColor(s);
                // Shorten for display
                const shortName = s === 'HS' ? 'H' : s === 'LS' ? 'L' : s === 'NS' ? 'N' : s;
                return (
                  <span
                    key={i}
                    className="text-xs font-bold"
                    style={{ color: staticColor }}
                    title={`Static to ${s}`}
                  >
                    {shortName}
                  </span>
                );
              })
            ) : (
              <span className="text-xs text-gray-500">-</span>
            )}
          </div>

          {/* Pilots in system - portraits */}
          {pilots.length > 0 && (
            <div className="flex items-center gap-0.5">
              {pilots.slice(0, 4).map((pilot) => (
                <img
                  key={pilot.characterId}
                  src={`https://images.evetech.net/characters/${pilot.characterId}/portrait?size=32`}
                  alt={pilot.characterName}
                  title={pilot.characterName}
                  className={`w-5 h-5 rounded-full border-2 ${
                    pilot.isOnline ? 'border-green-500' : 'border-gray-600'
                  }`}
                />
              ))}
              {pilots.length > 4 && (
                <span className="text-xs text-gray-400 ml-0.5">+{pilots.length - 4}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(SystemNodeComponent);
